/**
 * Presentational panel showing explainable, read-only booking suggestions for an
 * expense invoice. Selecting a suggestion only updates local parent state via
 * `onAddBooking`; it never saves automatically.
 */

import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useExpenseInvoiceBookingSuggestionsQuery } from '../Service/extendedExpenseInvoicesApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import type { BookingSuggestion } from '../Service/expenseInvoiceBookingSuggestions.contract';

export interface ExpenseInvoiceBookingSuggestionsPanelProps {
  expenseInvoiceId: number;
  assignedBookingIds: number[];
  onAddBooking: (bookingId: number) => void;
}

const confidenceLabels: Record<BookingSuggestion['confidence'], string> = {
  high: 'Wysokie dopasowanie',
  medium: 'Średnie dopasowanie',
  low: 'Niskie dopasowanie',
};

const confidenceColors: Record<BookingSuggestion['confidence'], 'success' | 'warning' | 'default'> = {
  high: 'success',
  medium: 'warning',
  low: 'default',
};

export const ExpenseInvoiceBookingSuggestionsPanel: React.FC<
  ExpenseInvoiceBookingSuggestionsPanelProps
> = ({ expenseInvoiceId, assignedBookingIds, onAddBooking }) => {
  const hasValidInvoiceId = Number.isFinite(expenseInvoiceId) && expenseInvoiceId > 0;
  const {
    data: suggestions,
    isLoading,
    isError,
    error,
  } = useExpenseInvoiceBookingSuggestionsQuery(expenseInvoiceId, {
    skip: !hasValidInvoiceId,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }} data-testid="booking-suggestions-loading">
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Wyszukiwanie sugestii bookingów...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }} data-testid="booking-suggestions-error">
        {extractApiErrorMessage(error, 'Nie udało się pobrać sugestii bookingów.')}
      </Alert>
    );
  }

  const assignedIds = new Set(assignedBookingIds);
  const visibleSuggestions = (suggestions ?? []).filter(
    (suggestion) => !assignedIds.has(suggestion.bookingId)
  );

  if (visibleSuggestions.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1 }}
        data-testid="booking-suggestions-empty"
      >
        Brak sugestii bookingów dla tej faktury.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }} data-testid="booking-suggestions-results">
      <Typography variant="body2" fontWeight="bold">
        Sugerowane bookingi
      </Typography>
      {visibleSuggestions.map((suggestion) => (
        <Box
          key={suggestion.bookingId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Booking #{suggestion.bookingId} - {suggestion.offerName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {suggestion.bookingDateFrom} → {suggestion.bookingDateTo} · {suggestion.numberOfParticipants} os.
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={confidenceLabels[suggestion.confidence]}
                color={confidenceColors[suggestion.confidence]}
              />
              {suggestion.reasons.map((reason) => (
                <Chip key={reason} size="small" variant="outlined" label={reason} />
              ))}
            </Stack>
          </Box>

          <Button
            size="small"
            variant="outlined"
            onClick={() => onAddBooking(suggestion.bookingId)}
          >
            Dodaj
          </Button>
        </Box>
      ))}
    </Box>
  );
};

export default ExpenseInvoiceBookingSuggestionsPanel;
